import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (token) => {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    await fetchUser();
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const requestMagicLink = async (email) => {
    try {
      console.log('🔵 AuthContext: Starting requestMagicLink for email:', email);
      console.log('🔵 AuthContext: API base URL:', api.defaults.baseURL);
      console.log('🔵 AuthContext: Making POST request to /auth/magic-link');
      
      const response = await api.post('/auth/magic-link', { email });
      
      console.log('✅ AuthContext: API request successful');
      console.log('🔵 AuthContext: Response status:', response.status);
      console.log('🔵 AuthContext: Response data:', response.data);
      
      return { success: true };
    } catch (error) {
      console.error('❌ AuthContext: API request failed');
      console.error('💥 AuthContext: Error object:', error);
      console.error('💥 AuthContext: Error response:', error.response);
      console.error('💥 AuthContext: Error message:', error.message);
      
      const errorMessage = error.response?.data?.detail || 'Failed to send magic link';
      console.error('💥 AuthContext: Final error message:', errorMessage);
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const verifyMagicLink = async (token) => {
    try {
      console.log('[DEBUG] Sending verification request with token:', token);
      
      // First try our test endpoint to see what's received
      try {
        const testResponse = await api.post('/auth/test-verify', { token });
        console.log('[DEBUG] Test endpoint response:', testResponse.data);
      } catch (testError) {
        console.error('[DEBUG] Test endpoint error:', testError);
      }
      
      // Now try the actual verification
      const response = await api.post('/auth/verify', { token });
      console.log('[DEBUG] Verification successful, received token:', response.data);
      await login(response.data.access_token);
      return { success: true };
    } catch (error) {
      console.error('[DEBUG] Verification error:', error.response?.data);
      const errorMessage = error.response?.data?.detail || 'Invalid or expired token';
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    requestMagicLink,
    verifyMagicLink,
    fetchUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
